import { useCallback, useMemo, useState } from "react";
import SimpleTableTemplate from "@/components/Templates/SimpleTableTemplate";
import { useSelectableTableView } from "@/utils/tableHelpers";
import { createTaskColumns } from "@/views/Risk/Tasks/tasksTableConfig";
import TasksDrawer from "@/views/Risk/Tasks/TasksDrawer";
import AddActionModal from "@/views/Risk/Tasks/AddActionModal";
import { useUserActions } from "@/hooks/useUserActions";
import type { TaskTableRow } from "@/views/Risk/Tasks/utils";

/**
 * Top-level Actions screen, gated by its own ACTIONS flag. It is the user's own
 * list: it starts empty and fills as they add findings from Trial News (the "+"
 * on a finding card) or add one by hand. The seeded RBQM records stay on
 * Quality > Tasks, which this screen deliberately no longer mirrors — the count
 * in the nav is meant to be "actions I have taken on", starting at zero.
 * The table, drawer and add-modal pieces are still shared with Tasks.
 */
export default function Actions() {
  const [addActionOpen, setAddActionOpen] = useState(false);
  const { actionRows, addManual, remove } = useUserActions();
  const { selectedRecord, setSelectedRecord, getClickableRowProps } =
    useSelectableTableView<TaskTableRow>();
  const columns = useMemo(() => createTaskColumns(actionRows), [actionRows]);

  const handleRemoveAction = useCallback(
    (task_id: string) => {
      remove(task_id);
      setSelectedRecord(null);
    },
    [remove, setSelectedRecord],
  );

  return (
    <div className="flex w-full min-w-0 flex-col p-4">
      <SimpleTableTemplate
        title="Actions"
        tooltipText="Review the actions assigned to you and your team, including linked issues, ownership, priority, due dates, and current status."
        fileName="actions"
        data={actionRows}
        columns={columns}
        rowKey="task_id"
        onRow={getClickableRowProps}
        onAddRecord={() => setAddActionOpen(true)}
        addButtonLabel="+ Add action"
      />
      {actionRows.length === 0 && (
        <p className="mt-2 text-[13px] text-[#8AA0A9]">
          Nothing here yet. Add a Trial News finding with its + button, or add
          an action directly.
        </p>
      )}
      <TasksDrawer
        selectedRecord={selectedRecord}
        setSelectedRecord={setSelectedRecord}
        onRemove={handleRemoveAction}
      />
      <AddActionModal
        open={addActionOpen}
        onClose={() => setAddActionOpen(false)}
        onCreate={addManual}
      />
    </div>
  );
}
